// Pricing migration 32 (ADO-3985), T14 to T16 and T19: what the side cart and the order review say
// about the multi-buy deals of the cart's lines. Pure.
//
// - An incomplete deal is one the shop has started (some of its cases in the cart) that gives no
//   free units yet. It is flagged (14: "You have 1 incomplete multi-buy"; 16 with several; 19 at
//   order review) unless the shop closed its flag and its progress has not changed since (R8,
//   00), and every one of its lines carries the nudge (14, A4: "Add 1 more · any of 3 … lines").
//   A deal past its first free case is not flagged again (a held decision).
// - WOW (R2 12w, 17w): the free cases show as a count on the line the server gave them to (row
//   31's rule: the deal's line with the most cases, the lowest SKU on a tie), with no € value.
// - Standard entries (R1 12s, 17s): no free case is worked out; a reached deal says BWG adds the
//   free stock, on the line the deal read names (`mostCasesSkuCode`: the same rule, applied by
//   pricing, D-2). With no line named (an older API) no line carries it.
// - After dropping below a deal (R3 21w, 21s) its lines say how to get the free case back, or
//   reach the deal again.
// The deal read's progress is the only count used; product and brand names from the boards' copy
// become the deal's own words or its lines' count (the story's deal-names decision), listed in the
// story as copy for the designer.

import type { MultibuyDeal } from '../api/multibuy-deals.client';
import { BWG_NOTE } from './dealView';
import { dealOfferText, lowestTier, tierText } from './multibuyDealText';
import { flagDismissed } from './useDealSessionStore';
import { formatDayMonth } from '../shared/dayMonth';

/** What a cart line holds, as both basket reads send it. */
export interface CartDealLine {
  readonly skuCode: string;
  readonly quantity: number;
  readonly freeUnitsEarned?: number;
  readonly multibuyOfferCode?: string | null;
}

export interface CartDealsContext {
  readonly wow: boolean;
  /** Deals the shop dropped below and has not reached again. */
  readonly lost: ReadonlySet<string>;
  /** Flags the shop closed, with each deal's pooled count then. */
  readonly dismissed?: ReadonlyMap<string, number>;
}

export interface CartLineStrip {
  /** `nudge`: incomplete; `reached`: the deal's free cases or BWG's note; `lost`: dropped below. */
  readonly kind: 'nudge' | 'reached' | 'lost';
  readonly text: string;
  /** The deal the strip opens; `null` for a WOW line's free count whose deal is not read (the
   *  count comes from the cart line, row 31; AC6 drops only what needs the deal read). */
  readonly deal: MultibuyDeal | null;
}

export interface CartDealsFlag {
  readonly deals: readonly MultibuyDeal[];
  readonly title: string;
  /** The side cart's line under the title (14, 16). */
  readonly body: string;
  /** The order review's (19): "… before you submit." */
  readonly reviewBody: string;
}

export interface CartDealToFinish {
  readonly deal: MultibuyDeal;
  /** R8 16b: "PANADOL PAIN RELIEF MIX & MATCH · 3 of 4 · add 1 more case". */
  readonly text: string;
}

export interface CartDealsView {
  readonly flag: CartDealsFlag | null;
  /** Every incomplete deal, flagged or not, by offer code (R8 16b's list). */
  readonly toFinish: readonly CartDealToFinish[];
  /** The strip under a side cart line (A4: after the name and stepper), or `null`. */
  lineStrip: (skuCode: string) => CartLineStrip | null;
  /** The order review line's pill (R2 17w, R1 17s), or `null`. */
  /** `tone`: `success` for the WOW free count (R2 17w: "Tone=Success"), `offer` for a standard
   *  entry's reached deal (R1 17s: the Promo pill relabelled). */
  reviewPill: (skuCode: string) => {
    readonly text: string;
    readonly note: string | null;
    readonly tone: 'success' | 'offer';
  } | null;
  /** The order review's in-line nudge for an incomplete deal (19). */
  reviewNudge: (deal: MultibuyDeal) => { readonly title: string; readonly body: string };
  /** The WOW free cases the lines carry (R2 13w, 18w), for the savings. */
  readonly freeCases: { readonly lines: number; readonly cases: number };
}

const freeCasesText = (count: number): string => `${count} free ${count === 1 ? 'case' : 'cases'}`;

/** How many more cases a started, unrewarded deal needs, or `null` when it is not incomplete. */
function shortBy(deal: MultibuyDeal): number | null {
  const { pooledQuantity, freeUnitsAtCurrent, unitsToNext } = deal.progress;
  if (deal.kind !== 'FREE_UNITS' || pooledQuantity === 0 || freeUnitsAtCurrent > 0) return null;
  return unitsToNext ?? null;
}

/** "3 of 4": the pooled cases of the server's next threshold (the web never works out deal
 *  arithmetic). */
function setProgress(deal: MultibuyDeal): string {
  const { pooledQuantity, nextThreshold } = deal.progress;
  return `${pooledQuantity} of ${nextThreshold ?? pooledQuantity}`;
}

function anyOfLines(deal: MultibuyDeal): string {
  const lines = deal.products.length;
  return lines === 1 ? '' : ` from any of the ${lines} lines`;
}

export function cartDealsOf(
  lines: readonly CartDealLine[],
  dealsBySku: Readonly<Record<string, readonly MultibuyDeal[]>>,
  context: CartDealsContext,
): CartDealsView {
  const inCart = new Map(
    lines.filter((line) => line.quantity > 0).map((line) => [line.skuCode, line]),
  );
  const deals = new Map<string, MultibuyDeal>();
  for (const sku of inCart.keys()) {
    for (const deal of dealsBySku[sku] ?? []) deals.set(deal.multibuyOfferCode, deal);
  }
  const ordered = [...deals.values()].sort((left, right) =>
    left.multibuyOfferCode < right.multibuyOfferCode ? -1 : 1,
  );
  const incomplete = ordered.filter((deal) => shortBy(deal) !== null);
  const flagged = incomplete.filter(
    (deal) =>
      !flagDismissed(
        context.dismissed ?? new Map(),
        deal.multibuyOfferCode,
        deal.progress.pooledQuantity,
      ),
  );

  const dealsOfLine = (sku: string): MultibuyDeal[] =>
    ordered.filter((deal) => deal.products.some((product) => product.skuCode === sku));

  const lineStrip = (sku: string): CartLineStrip | null => {
    const line = inCart.get(sku);
    if (line === undefined) return null;
    const own = dealsOfLine(sku);
    if (context.wow && (line.freeUnitsEarned ?? 0) > 0) {
      const holder = own.find((deal) => deal.multibuyOfferCode === line.multibuyOfferCode);
      return {
        kind: 'reached',
        text: `Deal reached · +${freeCasesText(line.freeUnitsEarned ?? 0)}`,
        deal: holder ?? null,
      };
    }
    for (const deal of own) {
      const short = shortBy(deal);
      if (short === null) continue;
      if (context.lost.has(deal.multibuyOfferCode)) {
        const goal = context.wow ? 'to get your free case back' : 'to reach the deal again';
        return {
          kind: 'lost',
          text: `${setProgress(deal)} · add ${short} more ${short === 1 ? 'case' : 'cases'} ${goal}`,
          deal,
        };
      }
      const lineCount = deal.products.length;
      return {
        kind: 'nudge',
        text:
          lineCount === 1 ? `Add ${short} more` : `Add ${short} more · any of ${lineCount} lines`,
        deal,
      };
    }
    if (!context.wow) {
      const reached = own.find(
        (deal) => deal.progress.freeUnitsAtCurrent > 0 && deal.progress.mostCasesSkuCode === sku,
      );
      if (reached !== undefined)
        return { kind: 'reached', text: `Deal reached · ${BWG_NOTE}`, deal: reached };
    }
    return null;
  };

  const reviewPill: CartDealsView['reviewPill'] = (sku) => {
    const strip = lineStrip(sku);
    if (strip?.kind !== 'reached') return null;
    if (context.wow) {
      const line = inCart.get(sku);
      return {
        text: `Multi-buy · +${freeCasesText(line?.freeUnitsEarned ?? 0)}`,
        note: null,
        tone: 'success',
      };
    }
    if (strip.deal === null) return null;
    const { pooledQuantity, unitsToNext } = strip.deal.progress;
    const buy = strip.deal.kind === 'FREE_UNITS' ? lowestTier(strip.deal).buyQuantity : null;
    // At a threshold, read off the server's progress (the story's D-2 exception).
    const atThreshold = buy !== null && unitsToNext === buy;
    return {
      text: atThreshold
        ? `Multi-buy · ${pooledQuantity} of ${pooledQuantity}`
        : 'Multi-buy · deal reached',
      note: BWG_NOTE,
      tone: 'offer',
    };
  };

  const reviewNudge = (deal: MultibuyDeal) => {
    const short = shortBy(deal) ?? 0;
    const tier = deal.kind === 'FREE_UNITS' ? lowestTier(deal) : null;
    const end = deal.displayTo === undefined ? null : formatDayMonth(deal.displayTo);
    const lineCount = deal.products.length;
    return {
      title:
        tier === null
          ? `Add ${short} more ${short === 1 ? 'case' : 'cases'}`
          : `Add ${short} more ${short === 1 ? 'case' : 'cases'} to get ${tier.freeQuantity} free`,
      body: [
        tier === null ? dealOfferText(deal) : tierText(tier),
        lineCount === 1 ? null : `mix and match any of ${lineCount} lines`,
        end === null ? null : `ends ${end}`,
      ]
        .filter((part): part is string => part !== null)
        .join(' · '),
    };
  };

  let flag: CartDealsFlag | null = null;
  if (flagged.length === 1) {
    const only = flagged[0] as MultibuyDeal;
    const short = shortBy(only) ?? 0;
    const body = `Add ${short} more ${short === 1 ? 'case' : 'cases'}${anyOfLines(only)} to get a free case`;
    flag = {
      deals: flagged,
      title: 'You have 1 incomplete multi-buy',
      body: `${body}.`,
      reviewBody: `${body} before you submit.`,
    };
  } else if (flagged.length > 1) {
    const body = `${flagged.length} deals are each a few cases short of a free case`;
    flag = {
      deals: flagged,
      title: `You have ${flagged.length} incomplete multi-buys`,
      body: `${body}.`,
      reviewBody: `${body} before you submit.`,
    };
  }

  const toFinish = incomplete.map((deal) => {
    const short = shortBy(deal) ?? 0;
    const name = deal.description ?? dealOfferText(deal);
    return {
      deal,
      text: `${name} · ${setProgress(deal)} · add ${short} more ${short === 1 ? 'case' : 'cases'}`,
    };
  });

  let freeLines = 0;
  let freeCases = 0;
  if (context.wow) {
    for (const line of inCart.values()) {
      if ((line.freeUnitsEarned ?? 0) > 0) {
        freeLines += 1;
        freeCases += line.freeUnitsEarned ?? 0;
      }
    }
  }

  return {
    flag,
    toFinish,
    lineStrip,
    reviewPill,
    reviewNudge,
    freeCases: { lines: freeLines, cases: freeCases },
  };
}
